import Notification from '../core/Notification';
import ClearIcon from '@material-ui/icons/Clear';
import RestoreIcon from '@material-ui/icons/Restore';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import DoneIcon from '@material-ui/icons/Done';
import ErrorIcon from '@material-ui/icons/ErrorOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import PropTypes from 'prop-types';
import React from 'react';
import DeleteAdornment from '../core/DeleteAdornment';
import FormError from '../core/FormError';
import { checkValidity, createLengthWarning, sanitiseText, lightSanitiseText } from '../core/utils/utils';
import ContentDisplay from './ContentDisplay';
import Datatype from './Datatype';
import { globalStyles as styles } from './style/gstheme';
import { DATATYPE_LIMITS, MEDIA_TYPES, MEDIA_TYPES_CONTENT } from './utils/constants';
import ReifyButton from './ReifyButton';
import { Typography } from '@material-ui/core';

class LiteralConfirmationField extends React.Component {

    constructor(props) {
        super(props);
        let isValidPattern = true;
        if (props.pattern && props.text !== "") {
            try {
                const re = new RegExp(this.props.pattern);
                isValidPattern = re.test(props.text);
            } catch (e) {
                isValidPattern = false;
            }
        }
        this.state = {
            originalText: props.text,
            text: props.text,
            showEdit: props.treatContentAs === undefined || props.treatContentAs === "text/plain",
            imageError: false,
            treatContentAs: props.treatContentAs,
            existingValues: [],
            existing: false,
            needsValidating: false,
            isValidPattern,
            error: !isValidPattern
        };
    }

    static getDerivedStateFromProps(props, state) {
        if (state.text && state.text !== "" && props.existingValues && props.existingValues.length > 0) {
            let hasChanged = true;
            if (props.existingValues.length === state.existingValues.length) {
                hasChanged = props.existingValues.some(ev => {
                    return !state.existingValues.some(sv => {
                        if (sv.text) {
                            return sv.text === ev.text && ev.datatype === sv.datatype;
                        }
                        else {
                            return sv === ev;
                        }
                    });
                });
            }
            else hasChanged = true;
            if (hasChanged) {
                return {
                    needsValidating: true,
                    existingValues: props.existingValues,
                    ...(props.text !== state.originalText) && {
                        text: props.text,
                        originalText: props.text
                    }
                };
            }
        }
        if (props.text !== state.originalText) {
            return {
                text: props.text,
                originalText: props.text
            };
        }
        if (props.treatContentAs !== state.treatContentAs) {
            return {
                needsValidating: true,
                treatContentAs: props.treatContentAs
            };
        }
        return null;
    }

    componentDidUpdate() {
        if (this.state.needsValidating && this.state.text && this.state.text !== "") this.checkValidity();
    }

    checkValidity() {
        const { datatype, existingValues, validationDatatype } = this.props;
        const text = this.state.text.toString().trim();
        if (text === "" || text === this.state.originalText) {
            this.setState({ error: false, existing: false, needsValidating: false });
            return;
        }
        const typeToValidateWith = validationDatatype || datatype;
        let validity = checkValidity(text, typeToValidateWith, existingValues, this.props.maxLength);
        let isValidPattern = true;
        if (validity.isValid && this.props.pattern) {
            try {
                const re = new RegExp(this.props.pattern);
                isValidPattern = re.test(text);
                validity.isValid = isValidPattern;
            } catch (e) {
                validity.isValid = false;
            }
        }
        this.setState({ error: !validity.isValid, isValidPattern, existing: validity.existing, needsValidating: false });
    }

    handleChange = (event) => {
        const t = event.target;
        let trimmedText = t.value.trimLeft();
        const isMultiline = [MEDIA_TYPES.MARKDOWN, MEDIA_TYPES.MATHML, MEDIA_TYPES.SVG, MEDIA_TYPES.HTML, MEDIA_TYPES.MULTILINE].includes(this.props.treatContentAs);
        if (!this.props.allowSpaces) {
            trimmedText = trimmedText.replace(/\s/g, '');
        }
        const cleanText = isMultiline ? lightSanitiseText(trimmedText) : sanitiseText(trimmedText);
        this.setState({ text: cleanText }, this.checkValidity);
    };

    reset = () => {
        let isValidPattern = true;
        if (this.props.pattern) {
            try {
                const re = new RegExp(this.props.pattern);
                isValidPattern = re.test(this.state.originalText);
            } catch (e) {
                isValidPattern = false;
            }
        }
        this.setState({
            text: this.state.originalText,
            isValidPattern,
            error: !isValidPattern,
            existing: false
        });
    };

    editDisplay = (showEdit) => {
        this.setState({ showEdit });
        if (!showEdit) {
            this.reset();
        }
    };

    loadImageError = () => this.setState({ imageError: true });

    clearText = () => this.setState({ text: "" });

    restoreText = () => this.setState({ text: this.state.originalText });

    handleUpdate = () => {
        this.props.onUpdate(this.state.text.trim());
    };

    handleDelete = () => {
        this.props.onDelete(this.state.text);
    };

    render() {

        const { classes, isReadOnly, label, canBeDeleted, margin, datatype, pattern, allowEmpty, treatContentAs, graph, ariaLabel,
            displayDatatype, invalidDatatype, dataTest, compact, placeHolder, reify, onReify, source, property, applicableStatements } = this.props;
        const { text, originalText, error, existing, isValidPattern, showEdit, imageError } = this.state;

        const maxLength = datatype && DATATYPE_LIMITS[datatype] && DATATYPE_LIMITS[datatype].maxLength ?
            this.props.maxLength && this.props.maxLength < DATATYPE_LIMITS[datatype].maxLength ?
                this.props.maxLength
                :
                DATATYPE_LIMITS[datatype].maxLength
            :
            this.props.maxLength;

        let icon = null;
        const isEdited = text.toString().trim() !== originalText.toString().trim();
        if (error || (existing && text.toString() !== originalText.toString())) {
            icon =
                <InputAdornment position="end">
                    <IconButton aria-label="Invalid value" title="The value is invalid" className={classes.smallIconButton}>
                        <ErrorIcon data-test="error" fontSize="small" />
                    </IconButton>
                </InputAdornment>;
        }
        else if (!isReadOnly && text.toString().trim().length > 0 && isEdited) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="Confirm change" onClick={this.handleUpdate}>
                        <IconButton aria-label="Confirm change" className={classes.smallIconButton}>
                            <DoneIcon data-test="done" fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>;
        }
        else if (!isReadOnly && canBeDeleted && !isEdited) {
            icon = <DeleteAdornment dataTest={dataTest} fontSize={treatContentAs && showEdit ? "medium" : "small"} onClick={this.handleDelete} />;
        }

        const isMultiline = [MEDIA_TYPES.MARKDOWN, MEDIA_TYPES.MATHML, MEDIA_TYPES.SVG, MEDIA_TYPES.HTML, MEDIA_TYPES.MULTILINE].includes(treatContentAs);
        const handleAsSpecialInput = treatContentAs && treatContentAs !== "text/plain";

        return (
            <ClickAwayListener onClickAway={text !== this.state.originalText || error ? this.reset : () => { }}>
                <div
                    data-test={dataTest + "-container"}>
                    {handleAsSpecialInput && showEdit &&
                        <div style={{
                            position: 'fixed',
                            backgroundColor: '#e0e0e0',
                            opacity: 0.3,
                            left: 0,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 11
                        }} />
                    }
                    <div style={
                        handleAsSpecialInput && showEdit ?
                            {
                                position: 'fixed',
                                backgroundColor: '#f0f0f0',
                                left: 10,
                                top: 70,
                                width: 'calc(50% - 50px)',
                                bottom: 10,
                                textAlign: 'center',
                                borderTopLeftRadius: 4,
                                borderBottomLeftRadius: 4,
                                borderLeft: 'solid 4px #c0c0c0',
                                borderTop: 'solid 4px #c0c0c0',
                                borderBottom: 'solid 4px #c0c0c0',
                                padding: 12,
                                zIndex: 12,
                                boxShadow: '1px 9px 49px -1px rgba(192,192,192,0.85)'
                            }
                            :
                            {}}>
                        {handleAsSpecialInput && showEdit &&
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    {isReadOnly ?
                                        <Typography data-test="view-only-value" variant="h2" className={classes.subMainHeading} style={{ textAlign: 'left' }}>View Value for {dataTest}</Typography>
                                        :
                                        <Typography data-test="edit-value" variant="h2" className={classes.subMainHeading} style={{ textAlign: 'left' }}>Edit Value for {dataTest}</Typography>
                                    }
                                    {!isReadOnly && <div style={{ display: 'flex' }}>
                                        {text !== "" &&
                                            <div>
                                                <Tooltip title="Clear text" onClick={this.clearText}>
                                                    <IconButton className={classes.smallIconButton}>
                                                        <ClearIcon data-test="clear-content-edit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        }
                                        {text !== originalText &&
                                            <div style={{ marginLeft: 6 }}>
                                                <Tooltip title="Restore text" onClick={this.restoreText}>
                                                    <IconButton className={classes.smallIconButton}>
                                                        <RestoreIcon data-test="restore-content-edit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        }
                                        {text !== "" &&
                                            <div style={{ marginTop: 14 }}>
                                                {icon}
                                            </div>
                                        }
                                    </div>
                                    }
                                </div>
                                <Typography data-test="pattern-expected" variant="caption" align="left" style={{ color: '#606060', transform: 'translateY(-12px)' }}>This property is expecting text of the form: {MEDIA_TYPES_CONTENT[treatContentAs]}</Typography>
                            </div>
                        }
                        <div style={reify && showEdit ?
                            {
                                display: 'flex',
                                ...(handleAsSpecialInput && showEdit) && {
                                    height: 'calc(100% - 50px)',
                                    overflow: 'scroll'
                                },
                            }
                            :
                            {
                                ...(handleAsSpecialInput && showEdit) && {
                                    height: 'calc(100% - 50px)',
                                    overflowY: 'scroll'
                                },
                            }}>
                            {reify && showEdit &&
                                <ReifyButton
                                    subject={source}
                                    property={property}
                                    graph={graph}
                                    isReadOnly={isReadOnly}
                                    value={{
                                        "@value": text,
                                        "type": datatype
                                    }}
                                    applicableStatements={applicableStatements}
                                    onReify={onReify} />
                            }
                            {(showEdit || imageError) &&
                                <TextField
                                    value={text}
                                    variant="outlined"
                                    style={(datatype && displayDatatype) || compact ?
                                        { marginTop: '0px' }
                                        :
                                        handleAsSpecialInput && showEdit ?
                                            { alignItems: 'start', marginBottom: 0 }
                                            :
                                            {}}
                                    margin={margin}
                                    onChange={this.handleChange}
                                    error={(!allowEmpty && text.toString().trim().length === 0) || text.toString().trim().length > maxLength || error || (existing && text.toString() !== originalText.toString())}
                                    label={label}
                                    fullWidth
                                    multiline={isMultiline}
                                    minRows={isMultiline ? 5 : 1}
                                    placeholder={allowEmpty ? placeHolder : "You must enter text"}
                                    onKeyDown={(ev) => {
                                        if (!error && !isReadOnly && !isMultiline && text.toString().trim().length > 0 && ev.key === 'Enter') {
                                            this.handleUpdate();
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                        }
                                    }}
                                    helperText={createLengthWarning(text, maxLength, isEdited)
                                        || (invalidDatatype ? <span data-test="helper-text">This value is no longer valid for the property's datatype.</span> : "")}
                                    InputProps={{
                                        className: classes.inputLabel,
                                        startAdornment: datatype && displayDatatype && !treatContentAs ?
                                            <React.Fragment>
                                                <Datatype
                                                    dataTest={dataTest + "-datatype"}
                                                    datatype={datatype}
                                                    invalid={invalidDatatype} />
                                                {datatype === "http://www.w3.org/2001/XMLSchema#anyURI" &&
                                                    <span data-test="visit-anyuri" style={{ transform: 'translateX(-6px)', marginRight: 6 }}>
                                                        <Tooltip title="Visit this URL">
                                                            <a aria-label="Vist this URL" href={text} target="_blank" rel="noreferrer">
                                                                <IconButton aria-label="Vist this URL" size="small">
                                                                    <OpenInNewIcon fontSize="small" />
                                                                </IconButton>
                                                            </a>
                                                        </Tooltip>
                                                    </span>
                                                }
                                            </React.Fragment>
                                            :
                                            null,
                                        endAdornment: handleAsSpecialInput && showEdit ? null : icon,
                                        inputProps: {
                                            "data-test": dataTest + "-input",
                                            "aria-label": ariaLabel,
                                            ...(error || (existing && text.toString() !== originalText.toString())) && {
                                                "aria-invalid": true,
                                                "aria-describedby": existing ?
                                                    "form-error"
                                                    :
                                                    dataTest + "-input-error",
                                            },
                                            readOnly: isReadOnly || invalidDatatype,
                                            maxLength: maxLength
                                        }
                                    }
                                    } />
                            }
                        </div>
                        {!isMultiline && typeof text === "string" && text.includes("\n") &&
                            <Notification error={false} text="The above value contains newlines in the original value but these are suppressed as this is not a multiline field." dataTest="not-multiline" />
                        }
                        {existing && text.toString() !== originalText.toString() && <FormError id="form-error">This value already exists.</FormError>}
                        {(error || text.toString().trim().length > maxLength) && !existing && datatype !== "" &&
                            <FormError id={dataTest + "-input-error"}                            >
                                {
                                    text.toString().trim().length > maxLength ?
                                        'Maximum is ' + maxLength + ' characters.'
                                        :
                                        isValidPattern ?
                                            'The value is not valid for the datatype.'
                                            :
                                            'The text must match: ' + pattern
                                }
                            </FormError>
                        }
                    </div>
                    {handleAsSpecialInput &&
                        <div style={
                            showEdit ?
                                {
                                    position: 'fixed',
                                    right: 10,
                                    top: 70,
                                    width: 'calc(50% - 38px)',
                                    bottom: 10,
                                    textAlign: 'center',
                                    borderTopRightRadius: 4,
                                    borderBottomRightRadius: 4,
                                    overflow: 'auto',
                                    borderRight: 'solid 4px #c0c0c0',
                                    borderTop: 'solid 4px #c0c0c0',
                                    borderBottom: 'solid 4px #c0c0c0',
                                    backgroundColor: 'white',
                                    padding: '12px 12px 12px 24px',
                                    zIndex: 12,
                                    boxShadow: '14px 2px 12px 11px rgba(192,192,192,0.25)'
                                }
                                :
                                {
                                    minHeight: 40,
                                    textAlign: 'center',
                                    borderRadius: 4,
                                    overflow: 'auto',
                                    border: 'solid 1px #c0c0c0',
                                    padding: 6,
                                    backgroundColor: 'white',
                                    marginBottom: 24
                                }}>
                            {showEdit ?
                                <div>
                                    <div style={{ float: 'right', width: 40, textAlign: 'right' }}>
                                        <Tooltip title={"Close Editing"}>
                                            <IconButton size="small" onClick={() => this.editDisplay(!showEdit)}>
                                                <VisibilityOffIcon data-test="hide-content" size="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                    <Typography variant="h2" className={classes.subMainHeading} style={{ textAlign: 'left', marginBottom: 12 }}>Preview</Typography>
                                </div>
                                :
                                <div style={{ float: 'right', width: 40, textAlign: 'right' }}>
                                    <Tooltip title={"Edit Content"}>
                                        <IconButton size="small" onClick={() => this.editDisplay(!showEdit)}>
                                            <VisibilityIcon data-test="show-content" size="small" />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            }
                            <div style={{
                                float: 'left',
                                overflow: 'auto',
                                ...(showEdit) && { height: 'calc(100% - 40px)' },
                                width: showEdit ? '100%' : 'calc(100% - 42px)'
                            }}>
                                <ContentDisplay
                                    maxHeight={showEdit ? "100%" : "200px"}
                                    key={"content-display"}
                                    text={text}
                                    treatContentAs={treatContentAs} />
                            </div>
                        </div>
                    }
                </div>
            </ClickAwayListener>
        );
    }
}

LiteralConfirmationField.defaultProps = {
    isReadOnly: false,
    canBeDeleted: false,
    margin: "normal",
    existingValues: [],
    displayDatatype: true,
    invalidDatatype: false,
    treatContentAs: undefined,
    compact: false,
    placeHolder: "",
    allowEmpty: false,
    allowSpaces: true,
    reify: false,
    maxLength: 1000
};

LiteralConfirmationField.propTypes = {
    dataTest: PropTypes.string,
    isReadOnly: PropTypes.bool,
    compact: PropTypes.bool,
    ariaLabel: PropTypes.string,
    margin: PropTypes.oneOf(['normal', 'dense']),
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    allowSpaces: PropTypes.bool,
    treatContentAs: PropTypes.string,
    allowEmpty: PropTypes.bool,
    placeHolder: PropTypes.string,
    text: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    invalidDatatype: PropTypes.bool,
    validationDatatype: PropTypes.string,
    existingValues: PropTypes.array,
    displayDatatype: PropTypes.bool,
    canBeDeleted: PropTypes.bool,
    datatype: PropTypes.string,
    maxLength: PropTypes.number,
    pattern: PropTypes.string,
    label: PropTypes.string,
    graph: PropTypes.string,
    reify: PropTypes.bool,
    onReify: PropTypes.func,
    source: PropTypes.string,
    property: PropTypes.string,
    applicableStatements: PropTypes.array,
    classes: PropTypes.object
};

export default withStyles(styles)(LiteralConfirmationField);